import styled from "styled-components";
import {
    Field,
    Filter,
    PrimitiveObject,
    QueryOrder,
    RefCondition,
    RefField,
    RefFieldLayout,
    SqlOperation
} from "../../../Types";
import {schemas} from "../../../config";
import {useAppDispatch, useAppSelector} from "../../../hooks";
import {useEffect, useMemo} from "react";
import {fetchTableByQuery, fetchTableRefEntity, switchToTablePK} from "../../../features/tableSlice";
import RefSingleRow from "./RefSingleRow";
import Loader from "../../Loader";
import {RootState} from "../../../store/store";
import RefTable from "./RefTable";
import {selectProjectSchema} from "../../../features/projectSlice";

interface PropsI {
    fieldSchema: Field & { type: "ref", ref: RefField },
    refKey: string,
    filters: Filter[]
    record: PrimitiveObject
}

export function createRefKey(filters: Filter[], limit: number, order: QueryOrder) {
    filters.sort((a, b) => a.field.localeCompare(b.field));
    return filters.map(f => f.field + f.filterOp + f.value).join("&") + "&limit=" + limit + "&order=" + order.field + "-way=" + order.way
}

export function refConditionToFilters(condition: RefCondition[], record: PrimitiveObject): Filter[] {
    return condition.map(c => ({
        field: c.foreignField || c.mainField,
        filterOp: c.op,
        value: c.staticValues !== undefined ? c.staticValues : (c.op === SqlOperation.NOT_IN || c.op === SqlOperation.IN) ? ([record[c.mainField]] as any) : record[c.mainField]
    }))
}

export const selectRefData =
    (entityName: string, refKey: string) =>
        (state: RootState) =>
            state.tables.refEntities[entityName][refKey];

export default function RefValueUI({fieldSchema, record, refKey, filters}: PropsI) {

    const projectSchema = useAppSelector(selectProjectSchema);
    const ref = fieldSchema.ref;
    const entityName = ref.entity;

    const dispatch = useAppDispatch();
    const data = useAppSelector(selectRefData(entityName, refKey));

    const {fields, refEntity, entityKeys} = useMemo(() => {
        if (!entityName || !projectSchema.refEntities)
            return {fields: undefined, refEntity: {table: ''}}
        const refEntity = projectSchema.refEntities![entityName];
        const allFields = Object.fromEntries(projectSchema.tables[refEntity.table].fields.map(x => ([x.key, x])));
        let fFields: Field[] = []
        const keys = !ref.keys ? refEntity.keys : refEntity.keys.filter(k => ref.keys?.includes(k));
        for (const key of keys) {
            if (allFields[key])
                fFields.push(allFields[key])
        }
        return {fields: fFields, refEntity: refEntity, entityKeys: refEntity.keys}
    }, [entityName, projectSchema.refEntities, projectSchema.tables, ref.keys])

    function fetch() {
        dispatch(fetchTableRefEntity({
            tableName: refEntity.table,
            pageIndex: 1,
            limit: ref.limit,
            fields: entityKeys || [],
            filters: filters,
            order: ref.order,
            refEntityName: entityName,
            refKey: refKey
        }))
    }


    useEffect(() => {
        if (!!data || !fields || ref.fetchMode === "superLazy")
            return
        dispatch(fetchTableRefEntity({
            tableName: refEntity.table,
            pageIndex: 1,
            limit: ref.limit,
            fields: entityKeys,
            filters: filters,
            order: ref.order,
            refEntityName: entityName,
            refKey: refKey
        }))
    }, [data, dispatch, entityKeys, entityName, fields, filters, ref.fetchMode, ref.limit, ref.order, refEntity, refEntity.table, refKey]);

    if (data && data.status === "loading") return <Empty><span><Loader/></span></Empty>;

    if (!data && fields && ref.fetchMode === "superLazy") return <Empty><span><button className={'btn-cmn'}
                                                                                      onClick={fetch}>Натисніть щоб завантажити</button></span></Empty>;

    if (!data || !fields || !data.open) return null;

    if (data.status === "error") return <Empty><span>Сталась помилка при завантаженні</span></Empty>;
    if (data.status === "loaded" && data.data.length === 0) return <Empty><span>Записів не знайдено</span></Empty>;

    return <SelectRefLayout data={data.data} fields={fields} layout={ref.layout} table={refEntity.table}
                            project={projectSchema.actName}/>
}

interface SRLPropsI {
    data: PrimitiveObject[],
    fields: Field[]
    layout: RefFieldLayout,
    table: string,
    project: string,
}

function SelectRefLayout({data, fields, layout, table, project}: SRLPropsI) {

    const dispatch = useAppDispatch();
    const searchByPk = (record: PrimitiveObject) => {
        dispatch(switchToTablePK({table, project, record}))
        dispatch(fetchTableByQuery({clean: true, pageIndex: 1, pages: 1}))
    };

    const pk = schemas[project].tables[table].pk;

    switch (layout) {
        case "vertical-list":
            return <>
                {data.map((r, i) =>
                    fields.map((f, fi) => (
                        <RefSingleRow
                            key={`${i ?? ''}-${f.key}`}   // safer key
                            value={r[f.key]}
                            first={fi === 0}
                            last={fi === fields.length - 1}
                            record={r}
                            schema={f}
                            // searchByPk={pk === f.key ? searchByPk : undefined}
                            searchByPk={undefined}
                        />
                    ))
                )}
            </>
        case "table":
            return <RefTable data={data} fields={fields} searchByPk={searchByPk}/>
        default:
            return <span>"NO METHOD FOR THIS LAYOUT TO RENDER REF VALUE - " + layout</span>
    }


}


const Container = styled.div`
    border-top: 2px solid var(--table-border-color);
    border-bottom: 2px solid var(--table-border-color);
`


const Empty = ({children}: any) => {

    return <EmptyWrap>
        <td colSpan={2}>
            {children}
        </td>
    </EmptyWrap>
}

const EmptyWrap = styled.tr`
    & td {
        & span {
            display: flex;
            width: 100%;
            height: 3rem;
            justify-content: center;
            align-items: center;
        }

    }
`
